import { useMemo } from "react";
import { useApolloClients } from "~/components/ApolloClients";
import { useGetDivisionsQuery } from "~/lib/federationGateway/graphql";
import type { Division } from "../types";

type FlatDivision = {
  id: string;
  name: string;
  parentId?: string | null;
};

/**
 * Build a nested `Division` tree from the flat list returned by the
 * `divisions` query, using `parentId` links. Divisions whose parent is not in
 * the list (or have no parent) become roots.
 *
 * Exported for unit testing — callers should use {@link useDivisions}.
 */
export function buildTree(flat: FlatDivision[]): Division[] {
  const byId = new Map<string, Division>();
  flat.forEach((d) =>
    byId.set(d.id, {
      id: d.id,
      name: d.name,
      parentId: d.parentId,
      children: [],
    }),
  );

  const roots: Division[] = [];
  byId.forEach((node) => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children!.push(node);
    else roots.push(node);
  });
  return roots;
}

/**
 * Fetches the organisation's divisions from the federation gateway (the same
 * `divisions` query used elsewhere in the platform) and exposes them in the two
 * shapes the Interview Guides dropdowns consume:
 *  - `divisions`     — nested `Division[]` for the hierarchical `DivisionSelect`
 *  - `divisionNames` — flat, sorted, de-duped names for the simple `<select>` filters
 */
export function useDivisions() {
  const { federationGatewayClient } = useApolloClients();

  const { data, loading } = useGetDivisionsQuery({
    client: federationGatewayClient,
    fetchPolicy: "cache-and-network",
  });

  const flat = (data?.divisions ?? []) as FlatDivision[];

  const divisions = useMemo(() => buildTree(flat), [data]);
  const divisionNames = useMemo(
    () => [...new Set(flat.map((d) => d.name))].sort(),
    [data],
  );

  return { divisions, divisionNames, loading };
}
